import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Modal, message } from './antd';
import {
  CloudArrowUp,
  File,
  X,
  CheckCircle,
  WarningCircle,
  Spinner,
  UploadSimple,
} from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import Button from '../../components/Button';
import { formatFileSize } from '../../components/portal/formatUtils';
import csrfHeaders from '../../utils/csrfHeaders';
import './uploadModal.scss';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const ARCHIVED_REFERRAL_ERROR = 'This referral is archived. Document uploads are disabled.';

const BLOCKED_EXTENSIONS = [
  '.exe', '.bat', '.cmd', '.sh', '.ps1', '.vbs', '.js', '.mjs',
  '.html', '.htm', '.svg', '.svgz', '.php', '.py', '.rb', '.pl',
  '.jar', '.war', '.msi', '.dll', '.so', '.app', '.dmg', '.scr',
  '.com', '.cpl', '.hta', '.wsf',
];

const FILE_STATUS = {
  PENDING: 'pending',
  VALIDATING: 'validating',
  VALID: 'valid',
  INVALID: 'invalid',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const getFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot).toLowerCase();
};

const validateFile = (file) => {
  const extension = getFileExtension(file.name);
  const errors = [];

  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File exceeds maximum size of 100 MB (${formatFileSize(file.size)})`);
  }

  if (BLOCKED_EXTENSIONS.includes(extension)) {
    errors.push(`File type "${extension}" is not allowed`);
  }

  return errors;
};

const isArchivedReferralError = (err) => {
  const responseData = err?.response?.data;

  if (err?.response?.status !== 422 || !responseData) return false;
  if (responseData.code === 'referral_archived') return true;

  return Array.isArray(responseData.results)
    && responseData.results.some((result) => result?.code === 'referral_archived');
};

const UploadModal = ({
  visible, onClose, referralEntitySlug, onUploadComplete,
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFiles([]);
    setUploading(false);
    setUploadComplete(false);
    setDragOver(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose();
  };

  const addFiles = useCallback((newFiles) => {
    const fileEntries = Array.from(newFiles).map((file) => {
      const errors = validateFile(file);
      return {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        name: file.name,
        size: file.size,
        status: errors.length > 0 ? FILE_STATUS.INVALID : FILE_STATUS.VALID,
        errors,
        serverError: null,
      };
    });
    setFiles((prev) => [...prev, ...fileEntries]);
  }, []);

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveFile = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleUpload = async () => {
    const validFiles = files.filter((f) => f.status === FILE_STATUS.VALID);
    if (validFiles.length === 0) {
      message.warning('No valid files to upload');
      return;
    }

    if (!referralEntitySlug) {
      message.error('Unable to upload: referral not fully initialized. Please refresh and try again.');
      return;
    }

    setUploading(true);

    // Mark valid files as uploading
    setFiles((prev) => prev.map((f) => (f.status === FILE_STATUS.VALID
      ? { ...f, status: FILE_STATUS.UPLOADING }
      : f)));

    try {
      const formData = new FormData();
      validFiles.forEach((f) => {
        formData.append('files[]', f.file);
      });

      const response = await axios.post(
        `/api/v1/referral_entities/${referralEntitySlug}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', ...csrfHeaders() } },
      );

      const { results } = response.data;

      // Match results back to files by filename
      setFiles((prev) => {
        const updatedFiles = [...prev];
        let resultIndex = 0;

        return updatedFiles.map((f) => {
          if (f.status === FILE_STATUS.UPLOADING) {
            const result = results[resultIndex];
            resultIndex += 1;

            if (result && result.success) {
              return { ...f, status: FILE_STATUS.SUCCESS };
            }
            return {
              ...f,
              status: FILE_STATUS.ERROR,
              serverError: result?.errors?.join(', ') || 'Upload failed',
            };
          }
          return f;
        });
      });

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (successCount > 0) {
        message.success(
          `${successCount} document${successCount > 1 ? 's' : ''} uploaded successfully${failCount > 0 ? `, ${failCount} failed` : ''}`,
        );
        onUploadComplete();
      } else {
        message.error('All uploads failed. Please try again.');
      }

      setUploadComplete(true);
    } catch (err) {
      console.error('Upload failed:', err);

      const archivedReferralError = isArchivedReferralError(err);
      const errorMessage = archivedReferralError ? ARCHIVED_REFERRAL_ERROR : 'Upload failed. Please try again.';
      message.error(errorMessage);

      setFiles((prev) => prev.map((f) => (f.status === FILE_STATUS.UPLOADING
        ? { ...f, status: FILE_STATUS.ERROR, serverError: errorMessage }
        : f)));
      setUploadComplete(true);
    } finally {
      setUploading(false);
    }
  };

  const validFileCount = files.filter((f) => f.status === FILE_STATUS.VALID).length;
  const hasFiles = files.length > 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case FILE_STATUS.SUCCESS:
        return <CheckCircle size={18} weight="fill" className="upload-modal__status-icon--success" />;
      case FILE_STATUS.ERROR:
      case FILE_STATUS.INVALID:
        return <WarningCircle size={18} weight="fill" className="upload-modal__status-icon--error" />;
      case FILE_STATUS.UPLOADING:
        return <Spinner size={18} className="upload-modal__status-icon--uploading" />;
      default:
        return <File size={18} className="upload-modal__status-icon--pending" />;
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      title={null}
      footer={null}
      closable={false}
      width={540}
      className="upload-modal"
      destroyOnClose
    >
      <div className="upload-modal__header">
        <Typography variant="text" size="large" bold>
          Upload Documents
        </Typography>
        <button type="button" className="upload-modal__close" onClick={handleClose} disabled={uploading}>
          <X size={20} />
        </button>
      </div>

      {/* Drag and Drop Zone */}
      <div
        className={`upload-modal__dropzone ${dragOver ? 'upload-modal__dropzone--active' : ''} ${hasFiles ? 'upload-modal__dropzone--compact' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !uploading) fileInputRef.current?.click();
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className="upload-modal__file-input"
          disabled={uploading}
        />
        <CloudArrowUp size={hasFiles ? 28 : 48} weight="duotone" className="upload-modal__dropzone-icon" />
        {!hasFiles ? (
          <>
            <Typography variant="text" size="small" bold className="upload-modal__dropzone-title">
              Drag and drop files here
            </Typography>
            <Typography variant="text" size="xsmall" className="upload-modal__dropzone-hint">
              or click to browse files. Maximum 100 MB per file.
            </Typography>
          </>
        ) : (
          <Typography variant="text" size="xsmall" className="upload-modal__dropzone-hint">
            Drop more files or click to browse
          </Typography>
        )}
      </div>

      {/* File List */}
      {hasFiles && (
        <div className="upload-modal__file-list">
          {files.map((fileEntry) => (
            <div
              key={fileEntry.id}
              className={`upload-modal__file-item upload-modal__file-item--${fileEntry.status}`}
            >
              <div className="upload-modal__file-status">
                {getStatusIcon(fileEntry.status)}
              </div>

              <div className="upload-modal__file-details">
                <Typography variant="text" size="small" className="upload-modal__file-name">
                  {fileEntry.name}
                </Typography>
                <Typography variant="text" size="xsmall" className="upload-modal__file-size">
                  {formatFileSize(fileEntry.size)}
                </Typography>
                {fileEntry.errors.length > 0 && (
                  <div className="upload-modal__file-errors">
                    {fileEntry.errors.map((err) => (
                      <Typography key={err} variant="text" size="xsmall" className="upload-modal__file-error">
                        {err}
                      </Typography>
                    ))}
                  </div>
                )}
                {fileEntry.serverError && (
                  <Typography variant="text" size="xsmall" className="upload-modal__file-error">
                    {fileEntry.serverError}
                  </Typography>
                )}
              </div>

              {!uploading && fileEntry.status !== FILE_STATUS.SUCCESS && (
                <button
                  type="button"
                  className="upload-modal__file-remove"
                  onClick={() => handleRemoveFile(fileEntry.id)}
                  title="Remove file"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="upload-modal__footer">
        <Button
          type="secondary"
          size="small"
          label={uploadComplete ? 'Close' : 'Cancel'}
          onClick={handleClose}
          disabled={uploading}
        />
        {!uploadComplete && (
          <Button
            type="primary"
            size="small"
            label={uploading ? 'Uploading...' : `Upload ${validFileCount > 0 ? `(${validFileCount})` : ''}`}
            onClick={handleUpload}
            disabled={validFileCount === 0 || uploading}
            leftIcon={uploading ? <Spinner size={14} /> : <UploadSimple size={14} weight="bold" />}
          />
        )}
      </div>
    </Modal>
  );
};

UploadModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  referralEntitySlug: PropTypes.string,
  onUploadComplete: PropTypes.func.isRequired,
};

UploadModal.defaultProps = {
  referralEntitySlug: null,
};

export default UploadModal;
